import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ElevatedButton from '../Layout/ElevatedButton';
import TwoFactorForm from './TwoFactorForm';
import TwoFactorPasswordReset from './TwoFactorPasswordReset';
import RegisterAccountFooter from 'components/Layout/RegisterAccountFooter';

const Form = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const AuthPage = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem 1rem;
  gap: 1rem;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
`;

const AuthContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
`;

const BackLink = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.5rem 0;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  &:hover {
    color: ${props => props.theme.text};
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid ${props => props.theme.buttonPrimary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem;
  margin: 0.5rem 0;
  border: 2px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.inputBorderFocus};
    background: ${props => props.theme.inputBackgroundFocus};
    box-shadow: 0 0 8px rgba(88, 101, 242, 0.3);
  }
`;

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const { login, twoFactorPending } = useAuth();
  const { prefetchData } = useData();
  const navigate = useNavigate();

  // If 2FA is pending, show the 2FA form
  if (twoFactorPending) {
    return <TwoFactorForm />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setLoginSuccess(false);

    const result = await login(email, password);
    
    if (result.success) {
      if (result.requires2FA) {
        // 2FA form will be shown automatically due to twoFactorPending state
        setLoading(false);
        return;
      } else {
        // Show success toast and set success state
        toast.success('Login successful! Welcome back.');
        setLoginSuccess(true);
        // Prefetch data in background without blocking navigation
        prefetchData();
        // Keep loading state to show user login is processing
        // The parent Login component will handle redirection when isAuthenticated becomes true
        // This is a fallback in case the state update is delayed
        setTimeout(() => {
          if (window.location.pathname === '/login') {
            setLoading(false);
            navigate('/dashboard', { replace: true });
          }
        }, 1000); // Brief delay to show success feedback
      }
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  const handleBypass = () => {
    // For development bypass, just navigate to dashboard
    // The AuthContext will handle development authentication
    navigate('/dashboard');
  };

  const handlePasswordResetSuccess = (message) => {
    setShowPasswordReset(false);
    setError(''); // Clear any existing errors
    // Show success message briefly, then return to login form
    setTimeout(() => {
      alert(message); // You could replace this with a proper toast notification
    }, 100);
  };

  const handlePasswordResetCancel = () => {
    setShowPasswordReset(false);
    setError(''); // Clear any existing errors
  };

  // If showing password reset form
  if (showPasswordReset) {
    return (
      <TwoFactorPasswordReset
        onSuccess={handlePasswordResetSuccess}
        onCancel={handlePasswordResetCancel}
      />
    );
  }

  // If 2FA is pending, show the 2FA form
  if (twoFactorPending) {
    return <TwoFactorForm />;
  }

  return (
    <AuthPage>
      <AuthContent>
        <BackLink onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Set temporary flag to allow access to homepage for authenticated users
          sessionStorage.setItem('bypassDashboardRedirect', 'true');
          navigate('/');
        }}>
          ← Back to Home
        </BackLink>

        <Form onSubmit={handleSubmit}>
        <h2 style={{ color: 'inherit', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>Login</h2>
        
        {error && (
          <div style={{ 
            padding: '0.75rem', 
            marginBottom: '1rem', 
            background: '#dc3545', 
            color: 'white', 
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
        />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
        />
        <ElevatedButton type="submit" style={{ width: '100%', marginTop: '1rem' }} disabled={loading}>
          {loading ? (loginSuccess ? 'Welcome back...' : 'Logging in...') : 'Login'}
        </ElevatedButton>

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={() => setShowPasswordReset(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#5865f2',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '0.9rem'
            }}
            disabled={loading}
          >
            Forgot Password?
          </button>
        </div>

        {/* Debug Bypass Button - Only in development */}
        {process.env.NODE_ENV !== 'production' && (
          <ElevatedButton
            type="button"
            onClick={handleBypass}
            style={{
              width: '100%',
              marginTop: '0.5rem',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              border: '2px solid #ff4757'
            }}
          >
            🚀 Debug Bypass (Skip Login)
          </ElevatedButton>
        )}

        </Form>
      </AuthContent>
      <RegisterAccountFooter />
    </AuthPage>
  );
};

export default LoginForm;